import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import StoreLayout from './StoreLayout';
import { ProductRail } from './ProductCard';
import { fetchProducts, useCategories, useProducts, useProductsByIds } from './catalogApi';
import { useRecentlyViewed } from './savedLists';
import { CORPORATE, STORE } from './storeConfig';
import { categoryLink, newestIds, retailPrice, useDocumentTitle } from './retailUtils';

const RAIL_SIZE = 12;
const CATEGORY_STRIP = 10;
const CATEGORY_RAILS = 3;
// "Shop by price" ceilings. The API's price filter works on MRP, so the counts do too.
const PRICE_BANDS = [499, 999, 1999, 4999];

/** A rail of one category's newest products, e.g. "Electronics". */
function CategoryRail({ category, newIds }) {
  const { products } = useProducts({ category: category.name, sort: 'newest' }, RAIL_SIZE);
  return (
    <ProductRail title={category.name} products={products} newIds={newIds}
      action={<Link to={categoryLink(category.name)} className="rt-text-link">View all{category.count ? ` ${category.count}` : ''}</Link>} />
  );
}

/** Live product counts for each price band, so every link leads somewhere useful. */
function usePriceBands() {
  const [bands, setBands] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.all(PRICE_BANDS.map(max => fetchProducts({ max }, 0, 1).then(r => ({ max, count: r.total }))))
      .then(list => alive && setBands(list.filter(b => b.count >= 3)))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return bands;
}

/** Layout follows the retail store (Myntra / Rare Rabbit): products on the first screen. */
export default function StoreHomePage() {
  useDocumentTitle(null);
  const categories = useCategories();
  const { products: latest, loading } = useProducts({ sort: 'newest' }, 48);
  const recentIds = useRecentlyViewed();
  const { products: recent } = useProductsByIds(recentIds.slice(0, RAIL_SIZE));
  const priceBands = usePriceBands();

  const inStock = useMemo(() => latest.filter(p => p.itemsAvailable !== 0), [latest]);
  const newIds = useMemo(() => newestIds(latest), [latest]);
  const newArrivals = inStock.slice(0, RAIL_SIZE);
  // Biggest discounts among recent additions (the API can't sort by discount).
  const topDeals = useMemo(() => inStock
    .map(p => ({ p, off: retailPrice(p).discountPercent }))
    .filter(x => x.off > 0)
    .sort((a, b) => b.off - a.off)
    .slice(0, RAIL_SIZE)
    .map(x => x.p), [inStock]);

  return (
    <StoreLayout wide>
      {/* Phones only: the desktop header already lists the categories. */}
      {categories.length > 1 && (
        <nav className="rt-pad rt-catstrip rt-only-mobile" aria-label="Shop by category">
          <Link to="/catalog" className="rt-catstrip-item">
            <span className="rt-catstrip-img rt-catstrip-img--all">All</span>
            <span className="rt-catstrip-label">Shop all</span>
          </Link>
          {categories.slice(0, CATEGORY_STRIP).map(c => (
            <Link key={c.name} to={categoryLink(c.name)} className="rt-catstrip-item">
              <span className="rt-catstrip-img">{c.image && <img src={c.image} alt="" />}</span>
              <span className="rt-catstrip-label">{c.name}</span>
            </Link>
          ))}
        </nav>
      )}

      {/* No banner images for the main store yet — the retail store's text hero stands in. */}
      <section className="rt-hero-text sf-hero">
        <div className="rt-eyebrow">{STORE.tagline}</div>
        <h1 className="rt-display">Brands you love, <span className="sf-accent-word">delivered</span></h1>
        <p className="sf-hero-text">{STORE.heroText}</p>
        <div className="sf-hero-actions">
          <Link to="/catalog" className="rt-btn">Shop all</Link>
          <Link to={CORPORATE.path} className="rt-btn rt-btn--ghost">{CORPORATE.linkLabel}</Link>
        </div>
      </section>

      <div className="rt-pad">
        {loading ? (
          <div className="rt-rail rt-rail-track" aria-hidden="true">
            {[...Array(6)].map((_, i) => <div key={i} className="rt-rail-item"><div className="rt-skel" style={{ aspectRatio: '3 / 4', borderRadius: 0 }} /></div>)}
          </div>
        ) : (
          <>
            <ProductRail title="New arrivals" products={newArrivals} newIds={newIds}
              action={<Link to="/catalog?sort=newest" className="rt-text-link">View all</Link>} />
            {priceBands.length > 1 && (
              <section className="rt-rail" aria-labelledby="rt-prices-title">
                <div className="rt-rail-head"><h2 id="rt-prices-title">Shop by price</h2></div>
                <div className="rt-prices">
                  {priceBands.map(b => (
                    <Link key={b.max} to={`/catalog?max=${b.max}`} className="rt-price-band">
                      <span className="rt-price-band-label">Under ₹{b.max.toLocaleString('en-IN')}</span>
                      <span className="rt-price-band-count">{b.count} {b.count === 1 ? 'product' : 'products'}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {categories.slice(0, CATEGORY_RAILS).map(c => <CategoryRail key={c.name} category={c} newIds={newIds} />)}
            <ProductRail title="Top deals" products={topDeals} newIds={newIds} />
            <ProductRail title="Recently viewed" products={recent} newIds={newIds} />
          </>
        )}
        <div style={{ textAlign: 'center', margin: '40px 0 8px' }}>
          <Link to="/catalog" className="rt-btn rt-btn--ghost">Browse all products</Link>
        </div>
      </div>

      {/* Companies have their own page; this band points them to it. */}
      <section className="sf-corp-band" aria-labelledby="sf-corp-band-title">
        <div className="rt-eyebrow">For business</div>
        <h2 id="sf-corp-band-title" className="rt-display">Bulk orders with your logo</h2>
        <p>Gifts and merchandise for your teams and clients, branded with your logo, with GST invoices and delivery to every office.</p>
        <Link to={CORPORATE.path} className="rt-btn rt-btn--ghost">Explore corporate orders</Link>
      </section>
    </StoreLayout>
  );
}
